/**
 * EstateIdentitySection — Identity block for the Estate entity dialog.
 *
 * Per docs/port-design/category-cards/estate.md:
 *   - Estate Name (e.g., "Estate of John Smith") — Field 6 (firstName) + Field 7 (lastName)
 *   - Decedent Name (derives "Estate of ..." prefix when officer uses the button)
 *   - Date of Death — drives DOB / Field 30 per Bill's §13d clarification
 *   - Case Number (blank for Affidavit) — drives Field 255
 *   - Case State / County (defaults to MO) — drives Field 256
 *   - Executor/Administrator name — Personal Rep placeholder display
 *   - Estate Tax ID (EIN) — Field 13 + Field 92 TinCode=E
 *   - Salutation locked to '*' (display read-only)
 *   - Call Class locked to '27' (display read-only)
 *
 * OMITS Citizenship, Employment, CIP scan panel — the estate itself has none
 * of those affordances (Personal Rep gets their own Individual dialog).
 *
 * customerType is driven by the derivation hook and published back to the
 * parent on mount (mirrors the Individual section's useEffect pattern).
 */

import { useEffect } from 'react';
import { Pill } from '@/components/primitives';
import type { RaoPerson, RaoTinCode } from '../../../types';
import type { DerivationResult } from './useEstateDerivation';
import {
  ESTATE_DEFAULT_STATE_CODE,
  ESTATE_HELPER_TEXT,
  MISSOURI_COUNTIES,
  US_STATES,
} from './estate-config';

// -----------------------------------------------------------------------------
// Shared leaf styles (lifted from Individual section for visual consistency)
// -----------------------------------------------------------------------------

const labelStyle: React.CSSProperties = {
  fontSize: 12,
  color: 'var(--ink-600)',
  display: 'block',
  marginBottom: 4,
};

const helperStyle: React.CSSProperties = {
  fontSize: 11,
  color: 'var(--ink-600)',
  marginTop: 2,
  lineHeight: 1.4,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  borderRadius: 4,
  border: '1px solid var(--ink-200)',
  fontSize: 14,
  boxSizing: 'border-box',
};

const readOnlyStyle: React.CSSProperties = {
  ...inputStyle,
  background: 'var(--field-readonly-bg, #f5f5f5)',
  color: 'var(--ink-700)',
};

function changedFieldStyle(base: React.CSSProperties, changed: boolean): React.CSSProperties {
  return changed
    ? { ...base, borderLeft: '3px solid var(--pcb-blue, #2968ad)', background: 'var(--field-changed-bg, #f0f7ff)', color: 'var(--ink-900, #1d1d1f)' }
    : base;
}

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

/** Split "Estate of John Smith" into decedent-name only ("John Smith"). */
function decedentFromEstateName(fullEstateName: string | undefined): string {
  if (!fullEstateName) return '';
  const trimmed = fullEstateName.trim();
  const m = /^estate of\s+/i.exec(trimmed);
  return m ? trimmed.slice(m[0].length) : trimmed;
}

function buildEstateNameFromDecedent(decedent: string): { firstName: string; lastName: string } {
  const clean = decedent.trim();
  if (!clean) return { firstName: 'Estate of', lastName: '' };
  return {
    firstName: 'Estate of',
    lastName: clean,
  };
}

// -----------------------------------------------------------------------------
// Props
// -----------------------------------------------------------------------------

export interface EstateIdentitySectionProps {
  person: RaoPerson;
  onChange: (patch: Partial<RaoPerson>) => void;
  mode: 'new' | 'existing';
  isChanged?: (field: string) => boolean;
  derivation: DerivationResult;
  readOnly?: boolean;
}

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

export function EstateIdentitySection({
  person,
  onChange,
  mode,
  isChanged,
  derivation,
  readOnly = false,
}: EstateIdentitySectionProps): JSX.Element {
  const isExisting = mode === 'existing';
  const ic = isChanged ?? ((_: string) => false);

  // Keep person.customerType synced with the derivation engine (Estate).
  // TinCode locked to 'E' (EIN) + callClass locked to '27' + salutation '*'.
  useEffect(() => {
    const patch: Partial<RaoPerson> = {};
    if (person.customerType !== derivation.customerType) {
      patch.customerType = derivation.customerType as RaoPerson['customerType'];
    }
    if (person.tinCode !== 'E') {
      patch.tinCode = 'E' as RaoTinCode;
    }
    if (person.callClass !== '27') {
      patch.callClass = '27';
    }
    // Salutation lives on extra — "*" per ENTITY-TYPE-PROCEDURE-ANALYSIS.md §5 line 351.
    const salutation =
      typeof person.extra?.salutation === 'string' ? (person.extra.salutation as string) : '';
    if (salutation !== '*') {
      patch.extra = { ...(person.extra ?? {}), salutation: '*' };
    }
    if (Object.keys(patch).length > 0) {
      onChange(patch);
    }
  }, [
    derivation.customerType,
    person.customerType,
    person.tinCode,
    person.callClass,
    person.extra,
    onChange,
  ]);

  // Executor / Administrator display name lives on extra.executorName (not a
  // real RaoPerson column). The "+ Link Personal Rep" UX that opens a nested
  // Individual dialog is out-of-scope for this agent; a follow-up ticket
  // wires it. We capture a display name here so the cart has something to show.
  const executorName =
    typeof person.extra?.executorName === 'string' ? (person.extra.executorName as string) : '';
  const executorCifId =
    typeof person.extra?.executorCifId === 'string' ? (person.extra.executorCifId as string) : '';

  // Decedent / case fields live on extra.
  const decedentName =
    typeof person.extra?.decedentName === 'string'
      ? (person.extra.decedentName as string)
      : decedentFromEstateName(person.lastName);
  const caseNumber =
    typeof person.extra?.idNumber === 'string' ? (person.extra.idNumber as string) : '';
  const caseState =
    typeof person.extra?.idState === 'string'
      ? (person.extra.idState as string)
      : ESTATE_DEFAULT_STATE_CODE;
  const caseCounty =
    typeof person.extra?.idCounty === 'string' ? (person.extra.idCounty as string) : '';

  const updateExtra = (patch: Record<string, unknown>): void => {
    onChange({ extra: { ...(person.extra ?? {}), ...patch } });
  };

  const handleDecedentNameChange = (next: string): void => {
    updateExtra({ decedentName: next });
    // Auto-populate Estate Name from decedent when the officer hasn't
    // manually overridden the firstName prefix.
    const { firstName, lastName } = buildEstateNameFromDecedent(next);
    onChange({
      firstName,
      lastName,
      extra: { ...(person.extra ?? {}), decedentName: next },
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Read-only entity type chip — mirrors Individual section's chip */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '10px 12px',
          background: 'var(--info-bg)',
          border: '1px solid var(--info-line)',
          borderRadius: 6,
        }}
      >
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-700)' }}>
          Entity Type:
        </span>
        <Pill variant="info">{derivation.customerType}</Pill>
        <span style={{ fontSize: 11, color: 'var(--ink-600)' }}>
          (fixed for this dialog — Call Class 27 · TinCode E)
        </span>
        {derivation.promotedFrom && (
          <span style={{ fontSize: 11, color: 'var(--pcb-blue)', marginLeft: 'auto' }}>
            updated from &lsquo;{derivation.promotedFrom}&rsquo;
          </span>
        )}
      </div>

      {/* Supervisor-approval gate — surfaced from derivation.reasons */}
      {derivation.requiresSupervisorApproval && derivation.approvalReasons.length > 0 && (
        <div
          role="alert"
          style={{
            padding: '10px 14px',
            background: 'var(--warn-bg)',
            border: '1px solid var(--warn-line)',
            borderRadius: 6,
            fontSize: 13,
            color: 'var(--ink-900)',
          }}
        >
          <strong>Supervisor approval required before commit:</strong>
          <ul style={{ margin: '6px 0 0 18px', padding: 0 }}>
            {derivation.approvalReasons.map((r, i) => (
              <li key={i} style={{ marginBottom: 2 }}>{r}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Decedent Name + Estate EIN row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label style={labelStyle} htmlFor="rao-est-decedent">Decedent name</label>
          <input
            id="rao-est-decedent"
            type="text"
            value={decedentName}
            onChange={(e) => handleDecedentNameChange(e.currentTarget.value)}
            readOnly={readOnly}
            style={changedFieldStyle(readOnly ? readOnlyStyle : inputStyle, ic('decedentName'))}
            placeholder="John Q. Smith"
            title="Decedent full legal name"
          />
          <div style={helperStyle}>{ESTATE_HELPER_TEXT.decedentName}</div>
        </div>
        <div>
          <label style={labelStyle} htmlFor="rao-est-ein">Estate EIN (Tax ID)</label>
          <input
            id="rao-est-ein"
            type="text"
            value={person.taxId ?? ''}
            onChange={(e) =>
              onChange({ taxId: e.currentTarget.value.replace(/\D/g, '').slice(0, 9) })
            }
            readOnly={isExisting || readOnly}
            style={changedFieldStyle(
              isExisting || readOnly ? readOnlyStyle : inputStyle,
              ic('taxId'),
            )}
            maxLength={9}
            placeholder="987654321"
            title="Estate EIN (9 digits, TinCode locked to E)"
          />
          <div style={helperStyle}>{ESTATE_HELPER_TEXT.estateEin}</div>
        </div>
      </div>

      {/* Estate Name row (derived from decedent; editable override) */}
      <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 12 }}>
        <div>
          <label style={labelStyle} htmlFor="rao-est-name-prefix">Estate Name prefix</label>
          <input
            id="rao-est-name-prefix"
            type="text"
            value={person.firstName ?? 'Estate of'}
            onChange={(e) => onChange({ firstName: e.currentTarget.value })}
            readOnly={readOnly}
            style={changedFieldStyle(readOnly ? readOnlyStyle : inputStyle, ic('firstName'))}
            title='Field 6 — typically "Estate of"'
          />
        </div>
        <div>
          <label style={labelStyle} htmlFor="rao-est-name-body">Estate Name body (decedent)</label>
          <input
            id="rao-est-name-body"
            type="text"
            value={person.lastName ?? ''}
            onChange={(e) => onChange({ lastName: e.currentTarget.value })}
            readOnly={readOnly}
            style={changedFieldStyle(readOnly ? readOnlyStyle : inputStyle, ic('lastName'))}
            title="Field 7 — decedent name portion"
          />
          <div style={helperStyle}>{ESTATE_HELPER_TEXT.estateName}</div>
        </div>
      </div>

      {/* Date of Death row (repurposes DOB / Field 30 per §13d) */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label style={labelStyle} htmlFor="rao-est-dod">
            Date of death
            <span style={{ color: 'var(--pcb-red, #b10505)', marginLeft: 4 }}>*</span>
          </label>
          <input
            id="rao-est-dod"
            type="date"
            value={person.dob ?? ''}
            onChange={(e) => onChange({ dob: e.currentTarget.value })}
            readOnly={readOnly}
            style={changedFieldStyle(readOnly ? readOnlyStyle : inputStyle, ic('dob'))}
            title="Decedent date of death — stored in Field 30 (DOB) per §13d"
          />
          <div style={helperStyle}>{ESTATE_HELPER_TEXT.dateOfDeath}</div>
        </div>
        <div>
          <label style={labelStyle} htmlFor="rao-est-case-num">Case number</label>
          <input
            id="rao-est-case-num"
            type="text"
            value={caseNumber}
            onChange={(e) => updateExtra({ idNumber: e.currentTarget.value })}
            readOnly={readOnly}
            style={changedFieldStyle(readOnly ? readOnlyStyle : inputStyle, ic('idNumber'))}
            placeholder="e.g., 23PH-PR00123"
            title="Probate court case number — Field 255"
          />
          <div style={helperStyle}>{ESTATE_HELPER_TEXT.caseNumber}</div>
        </div>
      </div>

      {/* Case state + county row */}
      <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 12 }}>
        <div>
          <label style={labelStyle} htmlFor="rao-est-case-state">Case state</label>
          <select
            id="rao-est-case-state"
            value={caseState}
            onChange={(e) => updateExtra({ idState: e.currentTarget.value })}
            disabled={readOnly}
            style={changedFieldStyle(readOnly ? readOnlyStyle : inputStyle, ic('idState'))}
            title="State where probate filed — Field 256"
          >
            {US_STATES.map((s) => (
              <option key={s.code} value={s.code}>
                {s.code} · {s.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label style={labelStyle} htmlFor="rao-est-case-county">Case county</label>
          <input
            id="rao-est-case-county"
            list="rao-est-mo-counties"
            type="text"
            value={caseCounty}
            onChange={(e) => updateExtra({ idCounty: e.currentTarget.value })}
            readOnly={readOnly}
            style={changedFieldStyle(readOnly ? readOnlyStyle : inputStyle, ic('idCounty'))}
            placeholder={caseState === 'MO' ? 'Phelps, Pulaski, …' : 'County name'}
            title="Probate county"
          />
          {caseState === 'MO' && (
            <datalist id="rao-est-mo-counties">
              {MISSOURI_COUNTIES.map((c) => (
                <option key={c} value={c} />
              ))}
            </datalist>
          )}
          <div style={helperStyle}>{ESTATE_HELPER_TEXT.caseCounty}</div>
        </div>
      </div>

      {/* Executor / Administrator display name (Personal Rep stub) */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 160px', gap: 12 }}>
        <div>
          <label style={labelStyle} htmlFor="rao-est-executor">
            Executor / Administrator name
          </label>
          <input
            id="rao-est-executor"
            type="text"
            value={executorName}
            onChange={(e) => updateExtra({ executorName: e.currentTarget.value })}
            readOnly={readOnly}
            style={changedFieldStyle(readOnly ? readOnlyStyle : inputStyle, ic('executorName'))}
            placeholder="Jane Smith"
            title="Personal Representative name (display only; CIF link is separate)"
          />
          <div style={helperStyle}>{ESTATE_HELPER_TEXT.executor}</div>
          {/*
            TODO(bill): wire a "+ Link Personal Rep" button that opens the
            Individual dialog (nested modal) and stores the resulting
            cifId on extra.executorCifId. Out of scope for this pass.
          */}
        </div>
        <div>
          <label style={labelStyle} htmlFor="rao-est-executor-cif">PR CIF</label>
          <input
            id="rao-est-executor-cif"
            type="text"
            value={executorCifId}
            onChange={(e) => updateExtra({ executorCifId: e.currentTarget.value })}
            readOnly={readOnly}
            style={changedFieldStyle(readOnly ? readOnlyStyle : inputStyle, ic('executorCifId'))}
            placeholder="CIF# (optional)"
            title="Personal Rep CIF link — wired by future linker UX"
          />
        </div>
      </div>

      {/* Salutation + Call Class + TinCode — all locked */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
        <div>
          <label style={labelStyle} htmlFor="rao-est-salutation">Salutation</label>
          <input
            id="rao-est-salutation"
            type="text"
            value="*"
            disabled
            style={readOnlyStyle}
            title="Estates use '*' per ENTITY-TYPE-PROCEDURE-ANALYSIS.md §5 line 351"
          />
        </div>
        <div>
          <label style={labelStyle} htmlFor="rao-est-callclass">Call class</label>
          <input
            id="rao-est-callclass"
            type="text"
            value="27"
            disabled
            style={readOnlyStyle}
            title="Estate Call Class locked to 27"
          />
        </div>
        <div>
          <label style={labelStyle} htmlFor="rao-est-tincode">TIN code</label>
          <input
            id="rao-est-tincode"
            type="text"
            value="E · EIN"
            disabled
            style={readOnlyStyle}
            title="Estate uses EIN (TinCode = E)"
          />
        </div>
      </div>
    </div>
  );
}
